import { useEffect, useState } from 'preact/hooks'
import { webinarRoomApi, type WebinarRoomHostLinkDto } from '@/api/webinarRoom'
import { useToast } from '@/hooks/useToast'

/** The OrbitalX-room web whose speakers get host links. */
export interface HostLinkRoom {
    workspaceId: string
    webId: string
}

export interface SpeakerHostLinks {
    /** Each speaker's host link by ContactId, the speaker key. Empty until loaded, and for a BigMarker web. */
    links: ReadonlyMap<string, WebinarRoomHostLinkDto>
    /** Why the links could not be read, or null. */
    error: string | null
    /** ContactIds whose host-link email is on its way. */
    sending: ReadonlySet<string>
    email: (contactId: string) => Promise<void>
}

const NO_LINKS: ReadonlyMap<string, WebinarRoomHostLinkDto> = new Map()

/**
 * The host links of an OrbitalX-room web's speakers (ORB-1086). Read again whenever the speakers change,
 * since a speaker added or removed gains or loses a link. Nothing is read without a room.
 */
export function useSpeakerHostLinks(room: HostLinkRoom | undefined, speakerIds: string[]): SpeakerHostLinks {
    const { showErrorUnless422 } = useToast()
    const [links, setLinks] = useState<ReadonlyMap<string, WebinarRoomHostLinkDto>>(NO_LINKS)
    const [error, setError] = useState<string | null>(null)
    const [sending, setSending] = useState<ReadonlySet<string>>(new Set())

    const workspaceId = room?.workspaceId
    const webId = room?.webId
    const speakersKey = speakerIds.join('|')

    useEffect(() => {
        if (!workspaceId || !webId) {
            setLinks(NO_LINKS)
            setError(null)
            return
        }
        let current = true
        void webinarRoomApi.getHostLinks(workspaceId, webId).then(res => {
            if (!current) return
            if (res.data) {
                setLinks(new Map(res.data.map(link => [link.ContactId, link])))
                setError(null)
            } else {
                setError(res.error || 'Unknown error')
            }
        })
        return () => { current = false }
    }, [workspaceId, webId, speakersKey])

    const email = async (contactId: string) => {
        if (!workspaceId || !webId) return
        setSending(prev => new Set(prev).add(contactId))
        try {
            const res = await webinarRoomApi.emailHostLink(workspaceId, webId, contactId)
            if (res.data) {
                const sent = res.data
                setLinks(prev => new Map(prev).set(sent.ContactId, sent))
            } else {
                showErrorUnless422('Email failed', res, 'Unable to email the host link')
            }
        } finally {
            setSending(prev => {
                const next = new Set(prev)
                next.delete(contactId)
                return next
            })
        }
    }

    return { links, error, sending, email }
}
